import type { AspectId, EncodingDefaults, ImagePlacement, PaletteId, QualityId, StatusId, TemplateId } from './types';

export const aspects: Record<AspectId, { label: string; width: number; height: number }> = {
  landscape: { label: '16:9', width: 1280, height: 720 },
  square: { label: '1:1', width: 1080, height: 1080 },
  story: { label: '9:16', width: 720, height: 1280 },
};

export const palettes: Record<
  PaletteId,
  {
    name: string;
    background: string;
    surface: string;
    text: string;
    muted: string;
    accent: string;
    accentTwo: string;
    isLight: boolean;
  }
> = {
  signal: {
    name: 'Signal',
    background: '#111214',
    surface: '#f4f7f6',
    text: '#fffaf2',
    muted: '#c8d5d2',
    accent: '#ff6b4a',
    accentTwo: '#2ec4b6',
    isLight: false,
  },
  studio: {
    name: 'Studio',
    background: '#fbfbf7',
    surface: '#1d1d1f',
    text: '#191919',
    muted: '#565656',
    accent: '#f9c74f',
    accentTwo: '#2a9d8f',
    isLight: true,
  },
  afterglow: {
    name: 'Afterglow',
    background: '#073b4c',
    surface: '#fff8eb',
    text: '#fff8eb',
    muted: '#c4e3e9',
    accent: '#ef476f',
    accentTwo: '#ffd166',
    isLight: false,
  },
  slatepop: {
    name: 'Slate Pop',
    background: '#202124',
    surface: '#f2f5f8',
    text: '#ffffff',
    muted: '#d2d6df',
    accent: '#9b5de5',
    accentTwo: '#00bbf9',
    isLight: false,
  },
};

export const qualityPresets: Record<QualityId, { label: string; description: string; fps: number; crf: number; audioKbps: number }> = {
  smallest: {
    label: 'Smallest',
    description: 'Smallest file',
    fps: 1,
    crf: 36,
    audioKbps: 64,
  },
  balanced: {
    label: 'Balanced',
    description: 'Best default',
    fps: 1,
    crf: 33,
    audioKbps: 96,
  },
  higher: {
    label: 'Higher',
    description: 'Sharper video',
    fps: 2,
    crf: 29,
    audioKbps: 128,
  },
};

export const qualityOrder: QualityId[] = ['smallest', 'balanced', 'higher'];

export const templates: Record<TemplateId, { label: string; description: string }> = {
  minimal: {
    label: 'Minimal',
    description: 'Quiet focus',
  },
  corporate: {
    label: 'Corporate',
    description: 'Clean update',
  },
  editorial: {
    label: 'Editorial',
    description: 'Magazine title',
  },
  podcast: {
    label: 'Podcast',
    description: 'Show art feel',
  },
  bold: {
    label: 'Bold',
    description: 'High impact',
  },
};

export const templateOrder: TemplateId[] = ['minimal', 'corporate', 'editorial', 'podcast', 'bold'];

export const imagePlacementDefaults: ImagePlacement = {
  fit: 'cover',
  zoom: 1,
  focalPoint: { x: 0.5, y: 0.5 },
};

export const placeholderTitles = [
  'The Annual Report of Unlikely Omens',
  'Minutes From the Committee for Minor Catastrophes',
  'A Brief History of Very Serious Nonsense',
  'The Ledger of Things Best Left Unexplained',
  'Notes From the Department of Sensible Magic',
  'The Extremely Official Goblet Update',
  'Small Talk With Large Consequences',
  'The Bureaucrat and the Thundercloud',
  'Memoirs of a Reluctant Oracle',
  'The Budget That Looked Back',
  'Proceedings of the Midnight Subcommittee',
  'How to File a Dragon',
  'The Unabridged Guide to Almost Knowing',
  'Reports From the Ministry of Mild Panic',
  'The Practical Uses of Impossible Things',
  'A Treatise on Hats, Power, and Biscuits',
  "The Auditor's Guide to Spontaneous Adventure",
  'An Update of Dubious Importance',
  'The Memo That Summoned Itself',
  'Everything Is Fine, Apart From the Magic',
] as const;

export function randomPlaceholderTitle() {
  return placeholderTitles[Math.floor(Math.random() * placeholderTitles.length)];
}

export const statusLabels: Record<StatusId, string> = {
  ready: 'Ready',
  reading: 'Reading file',
  encoding: 'Encoding',
  done: 'Done',
  failed: 'Failed',
};

export const encodingStatusPhrases = ['Encoding audio', 'Writing frames', 'Optimizing MP4'];
export const encodingStatusIntervalMs = 4200;

export const fallbackDefaults: EncodingDefaults = {
  quality: 'smallest',
  aspect: 'landscape',
  template: 'corporate',
};
